import React, { useState, useEffect } from 'react';
import axiosInstance from '../../utils/axiosConfig';
import { exceedsDecimals } from '../../utils/amountFormat';
import { QUANTITY_DECIMALS } from '../../utils/precision';

/**
 * BillsOfLadingTable — cargo-only (Jonas feedback 19.08: "many BLs for 1
 * delivery... we need to adjust final quantity"). Mirrors the fuels
 * per-product BDN table's replace-set save semantics.
 *
 * Each row: BL number, date, quantity, unit, estimated/final. "Save Bills
 * of Lading" POSTs the full set to set-bills-of-lading; on success the
 * summed quantity is reported back so DeliveryForm can write it into
 * bill_of_lading_quantity (every downstream reader keeps working).
 */
const BillsOfLadingTable = ({
  deliveryId,
  bills = [],
  units = [],
  onSavedFlash,
  onSumSaved,
}) => {
  const emptyRow = () => ({
    _key: `new-${Date.now()}-${Math.floor(Math.random() * 1e6)}`,
    bl_number: '',
    bl_date: '',
    quantity: '',
    quantity_unit: '',
    estimated_or_final: '',
  });

  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Seed from the loaded delivery; reseed when a different delivery loads.
  useEffect(() => {
    setRows(
      (bills || []).map((b) => ({
        _key: `db-${b.id}`,
        bl_number: b.bl_number || '',
        bl_date: b.bl_date || '',
        quantity: b.quantity != null ? String(b.quantity) : '',
        quantity_unit: b.quantity_unit || '',
        estimated_or_final: b.estimated_or_final || '',
      }))
    );
    setDirty(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryId, (bills || []).length]);

  const update = (key, field, value) => {
    setRows((prev) => prev.map((r) => (r._key === key ? { ...r, [field]: value } : r)));
    setDirty(true);
  };

  const addRow = () => {
    setRows((prev) => [...prev, emptyRow()]);
    setDirty(true);
  };

  const removeRow = (key) => {
    setRows((prev) => prev.filter((r) => r._key !== key));
    setDirty(true);
  };

  const sum = rows.reduce((acc, r) => {
    const n = parseFloat(String(r.quantity).replace(/\s/g, ''));
    return Number.isNaN(n) ? acc : acc + n;
  }, 0);

  const save = async () => {
    setSaving(true);
    try {
      const payload = rows.map((r) => ({
        bl_number: r.bl_number,
        bl_date: r.bl_date || null,
        quantity: r.quantity !== '' ? r.quantity : null,
        quantity_unit: r.quantity_unit || null,
        estimated_or_final: r.estimated_or_final || null,
      }));
      await axiosInstance.post(`/api/deliveries/${deliveryId}/set-bills-of-lading/`, {
        rows: payload,
      });
      setDirty(false);
      if (onSumSaved) onSumSaved(sum, rows);
      if (onSavedFlash) onSavedFlash('Bills of Lading saved.', true);
    } catch (error) {
      console.error('Error saving bills of lading:', error);
      if (onSavedFlash) {
        onSavedFlash(error.response?.data?.error || 'Failed to save Bills of Lading.', false);
      }
    } finally {
      setSaving(false);
    }
  };

  const cellInput = { width: '100%', padding: '6px 8px', border: '1px solid #d0d0d0', borderRadius: '4px' };

  return (
    <div className="form-group" style={{ marginTop: '15px' }}>
      <h3 className="delivery-subsection-title">
        Bills of Lading
        <span style={{ fontSize: '0.75rem', color: '#666', fontWeight: 'normal', marginLeft: '8px', fontStyle: 'italic' }}>
          (multiple BLs per delivery — B/L quantity becomes their sum)
        </span>
      </h3>

      {rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8px' }}>
            <thead>
              <tr style={{ textAlign: 'left', fontSize: '12px', color: '#555' }}>
                <th style={{ padding: '4px' }}>BL Number</th>
                <th style={{ padding: '4px' }}>BL Date</th>
                <th style={{ padding: '4px' }}>Quantity</th>
                <th style={{ padding: '4px' }}>Unit</th>
                <th style={{ padding: '4px' }}>Status</th>
                <th style={{ padding: '4px' }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r._key}>
                  <td style={{ padding: '4px' }}>
                    <input type="text" style={cellInput} value={r.bl_number}
                      onChange={(e) => update(r._key, 'bl_number', e.target.value)} placeholder="BL number" />
                  </td>
                  <td style={{ padding: '4px' }}>
                    <input type="date" style={cellInput} value={r.bl_date}
                      onChange={(e) => update(r._key, 'bl_date', e.target.value)} />
                  </td>
                  <td style={{ padding: '4px' }}>
                    <input type="text" inputMode="decimal" style={cellInput} value={r.quantity}
                      onChange={(e) => {
                        // A 7th decimal is refused at the keystroke, never shortened.
                        if (exceedsDecimals(e.target.value, QUANTITY_DECIMALS)) return;
                        update(r._key, 'quantity', e.target.value);
                      }} placeholder="Amount" />
                  </td>
                  <td style={{ padding: '4px' }}>
                    <select style={cellInput} value={r.quantity_unit}
                      onChange={(e) => update(r._key, 'quantity_unit', e.target.value)}>
                      <option value="">Unit</option>
                      {units.map((u) => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '4px' }}>
                    <select style={cellInput} value={r.estimated_or_final}
                      onChange={(e) => update(r._key, 'estimated_or_final', e.target.value)}>
                      <option value="">Status</option>
                      <option value="Estimated">Estimated</option>
                      <option value="Final">Final</option>
                    </select>
                  </td>
                  <td style={{ padding: '4px' }}>
                    <button type="button" onClick={() => removeRow(r._key)}
                      style={{ padding: '4px 8px', fontSize: '11px', background: '#fff0f0', border: '1px solid #ffd0d0', borderRadius: '3px', cursor: 'pointer' }}>
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button type="button" onClick={addRow}
          style={{ padding: '6px 12px', fontSize: '13px', background: '#f0f7ff', border: '1px solid #d0e3ff', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}>
          + Add BL
        </button>
        {rows.length > 0 && (
          <>
            <button type="button" onClick={save} disabled={saving || !dirty}
              style={{ padding: '6px 12px', fontSize: '13px', background: dirty ? '#5a7b35' : '#9bc4dd', color: 'white', border: 'none', borderRadius: '4px', cursor: dirty ? 'pointer' : 'default', fontWeight: 600 }}>
              {saving ? 'Saving…' : 'Save Bills of Lading'}
            </button>
            <span style={{ fontSize: '13px', color: '#444' }}>
              Total: <strong>{sum.toLocaleString('en-US', { maximumFractionDigits: QUANTITY_DECIMALS })}</strong>
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default BillsOfLadingTable;
